import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useChurch } from '../context/ChurchContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import useGlobalView from '../hooks/useGlobalView';
import { format } from 'date-fns';

/* ── QR Scanner Modal ── */
function QRScannerModal({ onScan, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    let active = true;
    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        streamRef.current = stream;
        if (videoRef.current && active) {
          const video = videoRef.current;
          video.srcObject = stream;
          video.onloadedmetadata = () => {
            video.play().catch(() => {});
            if (active) setScanning(true);
          };
        }
      } catch {
        setError("Impossible d'accéder à la caméra. Vérifiez les permissions du navigateur.");
      }
    }
    startCamera();
    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    if (!scanning) return;
    let jsQR;
    import('jsqr').then((m) => { jsQR = m.default; tick(); });

    function tick() {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      if (jsQR) {
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code && code.data) {
          cancelAnimationFrame(rafRef.current);
          onScan(code.data);
          return;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '100%', boxShadow: '0 24px 48px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontWeight: 800, color: '#1e293b' }}>Scanner carte QR</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: '#64748b', lineHeight: 1 }}>×</button>
        </div>
        {error ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#b91c1c', background: '#fef2f2', borderRadius: '10px', fontSize: '0.9rem' }}>{error}</div>
        ) : (
          <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', background: '#000' }}>
            <video ref={videoRef} style={{ width: '100%', display: 'block', maxHeight: '300px', objectFit: 'cover' }} muted playsInline />
            {/* Aiming crosshair overlay */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: '160px', height: '160px', border: '3px solid rgba(255,255,255,0.8)', borderRadius: '12px', boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' }} />
            </div>
          </div>
        )}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem', marginTop: '12px', marginBottom: 0 }}>
          Pointez la caméra vers le QR code sur la carte du fidèle
        </p>
      </div>
    </div>
  );
}

function IcoMan() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a7 7 0 0 1 13 0"/></svg>; }
function IcoWoman() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a7 7 0 0 1 13 0"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="9" y1="14" x2="15" y2="14"/></svg>; }
function IcoChild() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.5"/><path d="M7 21a5 5 0 0 1 10 0"/></svg>; }
function IcoStar() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>; }
function IcoCross() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><line x1="5" y1="7" x2="19" y2="7"/></svg>; }
function IcoHands() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/><path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2"/><path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/></svg>; }
function IcoGuest() { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>; }

const COUNT_FIELDS = [
  { key: 'hommes',    label: 'Hommes',    Icon: IcoMan,   color: '#3b82f6' },
  { key: 'femmes',    label: 'Femmes',    Icon: IcoWoman, color: '#ec4899' },
  { key: 'enfants',   label: 'Enfants',   Icon: IcoChild, color: '#f59e0b' },
  { key: 'nouveaux',  label: 'Nouveaux',  Icon: IcoStar,  color: '#8b5cf6' },
  { key: 'membres',   label: 'Membres',   Icon: IcoCross, color: '#1e40af' },
  { key: 'serviteurs',label: 'Serviteurs',Icon: IcoHands, color: '#10b981' },
  { key: 'invites',   label: 'Invités',   Icon: IcoGuest, color: '#f97316' },
];

const styles = {
  page: { width: '100%' },
  title: { fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: '20px' },
  card: {
    backgroundColor: 'var(--card-bg)', borderRadius: '12px',
    padding: '24px', border: '1px solid var(--border)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: '20px',
  },
  sectionTitle: { fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '14px' },
  select: {
    width: '100%', padding: '10px 14px', border: '1px solid var(--border)',
    borderRadius: '8px', fontSize: '0.95rem', color: 'var(--text-primary)',
    backgroundColor: 'var(--page-bg)', cursor: 'pointer', fontFamily: 'inherit',
  },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px' },
  tab: (active) => ({
    flex: 1, padding: '10px', cursor: 'pointer', fontWeight: 700,
    fontSize: '0.95rem', border: `2px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
    borderRadius: '8px', background: active ? 'var(--primary)' : 'var(--card-bg)',
    color: active ? 'white' : 'var(--text-secondary)', transition: 'all 0.15s',
    fontFamily: 'inherit',
  }),
  countGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '14px', marginBottom: '20px',
  },
  countCard: (color) => ({
    backgroundColor: 'var(--card-bg)', borderRadius: '12px',
    padding: '16px 10px', border: `2px solid ${color}30`,
    textAlign: 'center',
    boxShadow: `0 2px 8px ${color}15`,
  }),
  countIcon: { fontSize: '1.8rem', marginBottom: '4px' },
  countLabel: { fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 600, marginBottom: '8px' },
  countValue: (color) => ({
    fontSize: '2.2rem', fontWeight: 900, color, marginBottom: '8px', lineHeight: 1,
  }),
  bigBtn: (variant, color) => ({
    width: '42px', height: '42px', borderRadius: '8px',
    border: 'none', cursor: 'pointer', fontSize: '1.4rem', fontWeight: 800,
    backgroundColor: variant === 'plus' ? color : `${color}20`,
    color: variant === 'plus' ? 'white' : color,
    transition: 'transform 0.1s',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }),
  btnRow: { display: 'flex', gap: '8px', justifyContent: 'center' },
  totalBanner: {
    backgroundColor: 'var(--primary)', color: 'white', borderRadius: '10px',
    padding: '16px 24px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
    marginBottom: '16px',
  },
  saveBtn: {
    backgroundColor: 'white', color: 'var(--primary)', border: 'none',
    borderRadius: '8px', padding: '10px 24px', cursor: 'pointer',
    fontSize: '0.95rem', fontWeight: 700, fontFamily: 'inherit',
  },
  searchInput: {
    width: '100%', padding: '10px 14px', border: '1px solid var(--border)',
    borderRadius: '8px', fontSize: '0.95rem', marginBottom: '12px',
    background: 'var(--page-bg)', color: 'var(--text-primary)', fontFamily: 'inherit',
  },
  resultRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px', backgroundColor: 'var(--page-bg)',
    borderRadius: '8px', marginBottom: '6px', border: '1px solid var(--border)',
  },
  btnPrimary: {
    backgroundColor: 'var(--primary)', color: 'white', border: 'none',
    borderRadius: '6px', padding: '7px 14px', cursor: 'pointer',
    fontSize: '0.85rem', fontWeight: 700, fontFamily: 'inherit',
  },
  successFlash: {
    position: 'fixed', top: '80px', right: '20px', zIndex: 200,
    backgroundColor: '#22c55e', color: 'white', padding: '12px 24px',
    borderRadius: '10px', fontWeight: 700, fontSize: '1rem',
    boxShadow: '0 4px 12px rgba(34,197,94,0.4)',
    animation: 'fadeInOut 2s ease',
  },
};

export default function Attendance() {
  const { activeChurchId } = useChurch();
  const isGlobal = useGlobalView();
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [mode, setMode] = useState('bulk');
  const [counts, setCounts] = useState({ hommes: 0, femmes: 0, enfants: 0, nouveaux: 0, membres: 0, serviteurs: 0, invites: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [flash, setFlash] = useState('');
  // Inscrits à l'activité (AttendanceRecords existants)
  const [registeredList, setRegisteredList] = useState([]);
  const [loadingRegistered, setLoadingRegistered] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [pastSearch, setPastSearch] = useState('');
  const [pastResults, setPastResults] = useState([]);
  const [pastSearchLoading, setPastSearchLoading] = useState(false);
  const [showPastSearch, setShowPastSearch] = useState(false);

  const today = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/activities/?date_from=${today}&ordering=date,start_time&page_size=200`);
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        // Also get past 7 days activities
        const pastRes = await api.get(`/activities/?ordering=-date,-start_time&page_size=20`);
        const pastData = Array.isArray(pastRes.data) ? pastRes.data : pastRes.data.results || [];
        const combined = [...data, ...pastData.filter((a) => !data.find((b) => b.id === a.id))];
        setActivities(combined);
        if (data.length > 0) setSelectedActivity(String(data[0].id));
      } catch {
        toast.error('Erreur lors du chargement des activités.');
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, [today, activeChurchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recherche d'une activité passée par nom ou par date exacte (AAAA-MM-JJ),
  // pour retrouver une activité ancienne sans devoir charger tout l'historique.
  useEffect(() => {
    const q = pastSearch.trim();
    if (!q) { setPastResults([]); return; }
    setPastSearchLoading(true);
    const isDate = /^\d{4}-\d{2}-\d{2}$/.test(q);
    const params = isDate ? { date: q, page_size: 50 } : { search: q, page_size: 50, ordering: '-date,-start_time' };
    const timer = setTimeout(() => {
      api.get('/activities/', { params }).then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setPastResults(data);
      }).catch(() => setPastResults([])).finally(() => setPastSearchLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [pastSearch]);

  // Load existing bulk count when activity changes
  useEffect(() => {
    if (!selectedActivity) return;
    api.get(`/attendance/bulk/?activity=${selectedActivity}`).then((res) => {
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      if (data.length > 0) {
        const b = data[0];
        setCounts({ hommes: b.hommes, femmes: b.femmes, enfants: b.enfants, nouveaux: b.nouveaux, membres: b.membres, serviteurs: b.serviteurs, invites: b.invites });
      } else {
        setCounts({ hommes: 0, femmes: 0, enfants: 0, nouveaux: 0, membres: 0, serviteurs: 0, invites: 0 });
      }
    });
  }, [selectedActivity]);

  const handleCount = (key, delta) => {
    setCounts((prev) => ({ ...prev, [key]: Math.max(0, prev[key] + delta) }));
  };

  const showFlash = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(''), 2000);
  };

  const saveBulkCount = async () => {
    if (!selectedActivity) { toast.error('Veuillez sélectionner une activité.'); return; }
    setSaving(true);
    try {
      await api.post('/attendance/bulk/', { activity: parseInt(selectedActivity), ...counts });
      showFlash('Comptage enregistré !');
      toast.success('Comptage enregistré !');
    } catch {
      toast.error('Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const fetchRegistered = useCallback(async (activityId) => {
    if (!activityId) { setRegisteredList([]); return; }
    setLoadingRegistered(true);
    try {
      const res = await api.get(`/attendance/records/?activity=${activityId}&page_size=200`);
      const data = Array.isArray(res.data) ? res.data : res.data.results || [];
      setRegisteredList(data);
    } catch {
      setRegisteredList([]);
    } finally {
      setLoadingRegistered(false);
    }
  }, []);

  useEffect(() => {
    if (mode === 'individual') fetchRegistered(selectedActivity);
  }, [mode, selectedActivity, fetchRegistered]);

  const searchParticipants = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const res = await api.get(`/participants/?search=${encodeURIComponent(q)}&page_size=100`);
      setSearchResults(Array.isArray(res.data) ? res.data : res.data.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchParticipants(search), 350);
    return () => clearTimeout(timer);
  }, [search, searchParticipants]);

  const markPresent = async (participant) => {
    if (!selectedActivity) { toast.error('Veuillez sélectionner une activité.'); return; }
    try {
      const res = await api.post('/attendance/records/', {
        activity: parseInt(selectedActivity),
        participant: participant.id,
        is_anonymous: false,
      });
      if (res.data?.offline) {
        showFlash(`${participant.full_name} — enregistré hors-ligne`);
        toast('Hors-ligne : présence mise en file d\'attente.', { icon: '📶' });
      } else {
        showFlash(`${participant.full_name} présent(e) !`);
      }
      // Register background sync if supported
      if ('serviceWorker' in navigator && 'SyncManager' in window) {
        navigator.serviceWorker.ready.then((reg) => reg.sync.register('attendance-sync').catch(() => {}));
      }
      setSearch('');
      setSearchResults([]);
      fetchRegistered(selectedActivity);
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error('Ce participant est déjà enregistré pour cette activité.');
      } else {
        toast.error('Erreur lors de l\'enregistrement.');
      }
    }
  };

  const removePresent = async (record) => {
    try {
      await api.delete(`/attendance/records/${record.id}/`);
      showFlash('Présence supprimée.');
      fetchRegistered(selectedActivity);
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  };

  const handleQRScan = async (qrCode) => {
    setShowQR(false);
    if (!selectedActivity) { toast.error('Sélectionnez d\'abord une activité.'); return; }
    // UUID regex — QR code contient l'UUID du participant
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    try {
      let participant;
      if (uuidRegex.test(qrCode)) {
        const res = await api.get(`/participants/fidele/profile/${qrCode}/`);
        participant = res.data;
      } else {
        // Fallback: member_code or name search via fidele/login
        const res = await api.post('/participants/fidele/login/', { code: qrCode.trim().toUpperCase() });
        participant = res.data;
      }
      await markPresent(participant);
    } catch {
      toast.error('QR code invalide ou participant introuvable.');
    }
  };

  const total = counts.hommes + counts.femmes + counts.enfants;
  const currentActivity = activities.find((a) => String(a.id) === selectedActivity)
    || pastResults.find((a) => String(a.id) === selectedActivity);

  return (
    <div style={styles.page}>
      {showQR && <QRScannerModal onScan={handleQRScan} onClose={() => setShowQR(false)} />}
      {flash && <div style={styles.successFlash}>{flash}</div>}

      <h1 style={styles.title}>Enregistrer les présences</h1>

      {/* Activity selector */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Sélectionner l'activité</div>
        {loading ? (
          <LoadingSpinner size={24} />
        ) : (
          <select
            style={styles.select}
            value={selectedActivity}
            onChange={(e) => setSelectedActivity(e.target.value)}
          >
            <option value="">-- Choisir une activité --</option>
            {activities.map((act) => (
              <option key={act.id} value={act.id}>
                {act.date} · {act.start_time?.slice(0, 5)} · {act.name}{isGlobal ? ` — ${act.church_name || 'Non classé'}` : ''}
              </option>
            ))}
          </select>
        )}

        <button
          type="button"
          onClick={() => setShowPastSearch((v) => !v)}
          style={{ marginTop: '10px', background: 'none', border: 'none', color: '#2563eb', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
        >
          {showPastSearch ? '✕ Fermer la recherche' : '🔍 Chercher une activité plus ancienne…'}
        </button>

        {showPastSearch && (
          <div style={{ marginTop: '10px' }}>
            <input
              type="text"
              value={pastSearch}
              onChange={(e) => setPastSearch(e.target.value)}
              placeholder="Nom de l'activité, ou date exacte AAAA-MM-JJ"
              style={styles.select}
            />
            {pastSearchLoading && <div style={{ marginTop: '8px' }}><LoadingSpinner size={18} /></div>}
            {!pastSearchLoading && pastResults.length > 0 && (
              <div style={{ marginTop: '8px', maxHeight: '220px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                {pastResults.map((act) => (
                  <button
                    key={act.id}
                    type="button"
                    onClick={() => {
                      setSelectedActivity(String(act.id));
                      setShowPastSearch(false);
                      setPastSearch('');
                    }}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px',
                      background: 'none', border: 'none', borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer', fontSize: '0.85rem', color: '#1e293b',
                    }}
                  >
                    {act.date} · {act.start_time?.slice(0, 5)} · {act.name}{isGlobal ? ` — ${act.church_name || 'Non classé'}` : ''}
                  </button>
                ))}
              </div>
            )}
            {!pastSearchLoading && pastSearch.trim() && pastResults.length === 0 && (
              <div style={{ marginTop: '8px', fontSize: '0.82rem', color: '#94a3b8' }}>Aucune activité trouvée.</div>
            )}
          </div>
        )}

        {currentActivity && (
          <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            {currentActivity.location}
          </div>
        )}
      </div>

      {selectedActivity && (
        <>
          {/* Mode tabs */}
          <div style={styles.tabs}>
            <button style={styles.tab(mode === 'bulk')} onClick={() => setMode('bulk')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle' }}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              Comptage rapide
            </button>
            <button style={styles.tab(mode === 'individual')} onClick={() => setMode('individual')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle' }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Participant connu
            </button>
          </div>

          {mode === 'bulk' && (
            <div style={styles.card}>
              <div style={styles.sectionTitle}>Comptage par catégorie</div>
              <div style={styles.countGrid}>
                {COUNT_FIELDS.map((f) => (
                  <div key={f.key} style={styles.countCard(f.color)}>
                    <div style={{ ...styles.countIcon, color: f.color }}><f.Icon /></div>
                    <div style={styles.countLabel}>{f.label}</div>
                    <div style={styles.countValue(f.color)}>{counts[f.key]}</div>
                    <div style={styles.btnRow}>
                      <button
                        style={styles.bigBtn('minus', f.color)}
                        onClick={() => handleCount(f.key, -1)}
                        onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.9)')}
                        onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      >
                        −
                      </button>
                      <button
                        style={styles.bigBtn('plus', f.color)}
                        onClick={() => handleCount(f.key, 1)}
                        onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.9)')}
                        onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div style={styles.totalBanner}>
                <div>
                  <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '2px' }}>Total présences (Hommes + Femmes + Enfants)</div>
                  <div style={{ fontSize: '2.5rem', fontWeight: 900 }}>{total}</div>
                </div>
                <div style={{ fontSize: '0.88rem', opacity: 0.85, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {[
                      { key: 'hommes', label: 'H' }, { key: 'femmes', label: 'F' }, { key: 'enfants', label: 'E' },
                    ].filter(f => counts[f.key] > 0).map(f => (
                      <span key={f.key}>{counts[f.key]} {f.label}</span>
                    ))}
                  </div>
                  {[
                    { key: 'nouveaux', label: 'Nv' }, { key: 'membres', label: 'Mb' },
                    { key: 'serviteurs', label: 'Sv' }, { key: 'invites', label: 'Inv' },
                  ].some(f => counts[f.key] > 0) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '0.76rem', opacity: 0.75 }}>
                      <span style={{ fontStyle: 'italic' }}>déjà inclus ci-dessus :</span>
                      {[
                        { key: 'nouveaux', label: 'Nv' }, { key: 'membres', label: 'Mb' },
                        { key: 'serviteurs', label: 'Sv' }, { key: 'invites', label: 'Inv' },
                      ].filter(f => counts[f.key] > 0).map(f => (
                        <span key={f.key}>{counts[f.key]} {f.label}</span>
                      ))}
                    </div>
                  )}
                </div>
                <button style={styles.saveBtn} onClick={saveBulkCount} disabled={saving}>
                  {saving ? 'Enregistrement...' : 'Enregistrer le comptage'}
                </button>
              </div>
            </div>
          )}

          {mode === 'individual' && (
            <div style={styles.card}>
              {/* Déjà présents */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={styles.sectionTitle}>
                    Présents enregistrés
                    {registeredList.length > 0 && (
                      <span style={{ marginLeft: '8px', backgroundColor: '#1e40af', color: 'white', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px' }}>
                        {registeredList.length}
                      </span>
                    )}
                  </div>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                    onClick={() => fetchRegistered(selectedActivity)}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                    Actualiser
                  </button>
                </div>

                {loadingRegistered ? (
                  <LoadingSpinner size={24} />
                ) : registeredList.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: '0.88rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    Aucune présence enregistrée pour cette activité.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '280px', overflowY: 'auto' }}>
                    {registeredList.map((record) => {
                      const p = record.participant_detail || {};
                      const name = p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—';
                      const isAnon = record.is_anonymous;
                      return (
                        <div key={record.id} style={{ ...styles.resultRow, borderLeft: `4px solid ${isAnon ? '#94a3b8' : '#22c55e'}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '34px', height: '34px', borderRadius: '50%', backgroundColor: isAnon ? '#f1f5f9' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {isAnon ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 1 0-16 0"/></svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                              )}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                                {isAnon ? 'Anonyme' : name}
                              </div>
                              {!isAnon && (p.member_code || p.status) && (
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  {p.member_code && <span style={{ fontFamily: 'monospace' }}>{p.member_code}</span>}
                                  {p.status && <span> · {p.status}</span>}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                            onClick={() => removePresent(record)}
                            title="Supprimer cette présence">
                            Retirer
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Séparateur */}
              <div style={{ height: '1px', backgroundColor: '#e2e8f0', margin: '16px 0' }} />

              {/* Ajouter un participant */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={styles.sectionTitle}>Ajouter un participant</div>
                <button
                  onClick={() => setShowQR(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', background: '#1e40af', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="5" height="5"/><rect x="16" y="3" width="5" height="5"/><rect x="3" y="16" width="5" height="5"/><line x1="21" y1="16" x2="21" y2="21"/><line x1="16" y1="21" x2="21" y2="21"/><line x1="16" y1="16" x2="16" y2="16"/></svg>
                  Scanner QR
                </button>
              </div>
              <input
                style={styles.searchInput}
                placeholder="Nom, prénom ou numéro de téléphone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {searchLoading && <LoadingSpinner size={24} />}
              {!searchLoading && search.trim() && searchResults.length === 0 && (
                <div style={{ color: '#94a3b8', fontSize: '0.9rem', textAlign: 'center', padding: '20px' }}>
                  Aucun participant trouvé pour "{search}"
                </div>
              )}
              {searchResults.map((p) => {
                const alreadyIn = registeredList.some(r => r.participant === p.id || r.participant_detail?.id === p.id);
                return (
                  <div key={p.id} style={{ ...styles.resultRow, opacity: alreadyIn ? 0.6 : 1 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#1e293b' }}>{p.full_name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                        {p.gender} · {p.status}{p.phone ? ` · ${p.phone}` : ''}
                      </div>
                    </div>
                    {alreadyIn ? (
                      <span style={{ fontSize: '0.82rem', color: '#16a34a', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                        Déjà présent
                      </span>
                    ) : (
                      <button style={styles.btnPrimary} onClick={() => markPresent(p)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '4px', verticalAlign: 'middle' }}><polyline points="20 6 9 17 4 12"/></svg>
                        Présent
                      </button>
                    )}
                  </div>
                );
              })}
              {!search.trim() && (
                <div style={{ textAlign: 'center', padding: '16px', color: '#94a3b8', fontSize: '0.88rem' }}>
                  Tapez un nom pour rechercher un participant à ajouter...
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
